"use client"

import { create } from "zustand"
import { persist, createJSONStorage, type StateStorage } from "zustand/middleware"
import { createClient, resetClient } from "@/lib/supabase/client"
import { syncAllCredentialsToAccount } from "@/lib/sync-credentials"
import { hasPremiumAccess } from "@/lib/entitlements"
import type { User } from "@supabase/supabase-js"

export type UserRole = "user" | "moderator" | "admin" | "dev" | "super_admin"

export interface UserProfile {
  id: string
  username: string
  display_name?: string
  email: string
  profile_picture_url?: string
  badge_frame_url?: string | null
  pinned_badge_slugs?: string[]
  role: UserRole
  is_verified: boolean
  is_banned: boolean
  trust_score: number
  reputation_level: string
  created_at: string
  // Creator identity
  creator_mode?: boolean
  creator_tags?: string[]
  creator_mode_enabled_at?: string
  custom_slug?: string | null
  // Discord
  discord_id?: string
  discord_username?: string
  // Subscription — profile is fetched via select("*") in app/api/auth/profile,
  // so these are always present at runtime even though they were missing here.
  subscription_tier?: string
  subscription_status?: string
  // "stripe" | "patreon" | null: where the paid plan comes from (Patreon Plan C, lib/patreon/entitlement.ts)
  subscription_source?: string | null
  subscription_started_at?: string | null
  is_founder?: boolean
  founder_slot_number?: number | null
  founder_since?: string | null
  goop_balance?: number
  ai_credits?: number
  // Version string of the last "What's New" patch note this account
  // dismissed (components/patch-notes-modal.tsx) — account-scoped so it
  // doesn't reappear on a different browser/device.
  last_seen_patch_version?: string | null
}

interface AuthState {
  user: User | null
  profile: UserProfile | null
  isLoading: boolean
  isGuest: boolean
  // True only once THIS page load has gotten a definitive answer from
  // Supabase this session (a real session, or genuinely no session) — never
  // persisted, always starts false on a fresh load. `user`/`profile` are
  // persisted to localStorage for instant paint on reload, but that cached
  // value is not proof anyone is actually still signed in: the real session
  // cookie can have expired or been cleared independently of localStorage.
  // Account-gated popups (achievement unlocks, etc.) must gate on this, not
  // on `user` alone, or a stale cached identity can trigger them for a
  // visitor who isn't actually signed in. See initialize() below — the
  // timeout/error paths deliberately leave this false, since they couldn't
  // actually confirm anything either way.
  sessionVerified: boolean
  // Set once by the daily-login-check fire-and-forget call below when it
  // actually awarded Gooncoin — GooncoinAddedPopup (mounted once, globally)
  // reads and clears this. Deliberately not persisted (see partialize
  // below): a page reload should never re-show a claim that already fired.
  dailyGooncoinClaim: { amount: number; newBalance: number } | null
  setDailyGooncoinClaim: (claim: { amount: number; newBalance: number } | null) => void
  setUser: (user: User | null) => void
  setProfile: (profile: UserProfile | null) => void
  setLoading: (loading: boolean) => void
  initialize: () => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: (forceUser?: User) => Promise<void>
}

const DEBUG_AUTH = process.env.NEXT_PUBLIC_DEBUG_AUTH === "1"
const log = (...args: any[]) => { if (DEBUG_AUTH) console.log("[AUTH]", ...args) }
const warn = (...args: any[]) => { if (DEBUG_AUTH) console.warn("[AUTH WARN]", ...args) }
const err = (...args: any[]) => console.error("[AUTH ERROR]", ...args)

// A QuotaExceededError here is almost always some *other* localStorage-based
// feature having filled up the origin's shared quota, not this store's own
// (small) payload — that shouldn't take auth down with it. Swallow the write
// failure rather than let it surface as an uncaught error; the session still
// lives server-side, so losing this client-side cache just means one extra
// profile fetch on next load, not a broken session.
const quotaSafeStorage: StateStorage = {
  getItem: (name) => localStorage.getItem(name),
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value)
    } catch (error) {
      warn("Skipping auth cache write — storage quota issue (likely another feature's cache), not fatal:", error)
    }
  },
  removeItem: (name) => localStorage.removeItem(name),
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      profile: null,
      isLoading: true,
      isGuest: true,
      sessionVerified: false,
      dailyGooncoinClaim: null,

      setDailyGooncoinClaim: (claim) => set({ dailyGooncoinClaim: claim }),

      setUser: (user) => {
        log("setUser called ->", user?.email ?? "null", "| isGuest ->", !user)
        set({ user, isGuest: !user })
      },

      setProfile: (profile) => {
        log("setProfile called ->", profile ? `${profile.username} role=${profile.role}` : "null")
        set({ profile })
      },

      setLoading: (isLoading) => set({ isLoading }),

      initialize: async () => {
        log("=== initialize() START ===")
        set({ isLoading: true, sessionVerified: false })

        // CRITICAL: Check if we need to clear cached auth state (OAuth login)
        if (typeof window !== "undefined") {
          const urlParams = new URLSearchParams(window.location.search)
          if (urlParams.get("clear_auth_cache") === "1") {
            log("🔄 OAuth callback detected — clearing cached auth state to prevent account switching")
            localStorage.removeItem("goonru-auth-storage")
            // Remove the query param so it doesn't trigger again on refresh
            const newUrl = new URL(window.location.href)
            newUrl.searchParams.delete("clear_auth_cache")
            window.history.replaceState({}, "", newUrl.toString())
          }

          const authStorage = localStorage.getItem("goonru-auth-storage")
          if (!authStorage) {
            log("No goonru-auth-storage found — user logged out or first visit")
            // Don't set guest mode yet - let getSession() determine the actual state
            // This is important for OAuth flows where session exists but cache doesn't
          }
        }

        // Shorter timeout for better UX - 6 seconds instead of 10
        const timeoutId = setTimeout(() => {
          warn("Initialization TIMEOUT after 6s — preserving existing state, not forcing guest mode")
          const current = get()
          // Only force guest if we genuinely have no user — don't wipe a valid session.
          // Either way, sessionVerified stays false: a timeout means Supabase never
          // actually confirmed anything, so any cached `user` here is still just an
          // unverified guess, not proof this browser is genuinely signed in.
          if (!current.user) {
            set({ user: null, profile: null, isGuest: true, isLoading: false })
          } else {
            set({ isLoading: false })
          }
        }, 6000)

        try {
          const supabase = createClient()
          const {
            data: { session },
            error: sessionError,
          } = await supabase.auth.getSession()
          log("getSession() ->", session?.user?.email ?? "no session", "| error:", sessionError?.message ?? "none")

          if (session?.user) {
            // getSession() only reads the token cached in localStorage — it
            // does NOT confirm with Supabase's Auth server that the token is
            // still valid. A session that ended elsewhere (revoked, signed
            // out on another device, naturally expired) still passes this
            // check, so the app believed it was logged in while the server
            // correctly 401'd every authenticated request (e.g. "Failed to
            // fetch followed companions: Unauthorized"). getUser() does a
            // real round-trip and is the only way to know the session is
            // actually still good — verify before trusting it.
            log("Session found in local cache, verifying with getUser() before trusting it")
            const {
              data: { user: verifiedUser },
              error: verifyError,
            } = await supabase.auth.getUser()
            log("getUser() verification ->", verifiedUser?.email ?? "invalid/expired session", "| error:", verifyError?.message ?? "none")

            if (verifiedUser) {
              set({ user: verifiedUser, isGuest: false, sessionVerified: true })
              await get().refreshProfile(verifiedUser)
            } else {
              warn("Cached session failed server verification — clearing stale local session")
              await supabase.auth.signOut()
              set({ user: null, profile: null, isGuest: true, sessionVerified: true })
            }
          } else {
            log("No session — trying getUser()")
            const {
              data: { user },
              error: userError,
            } = await supabase.auth.getUser()
            log("getUser() ->", user?.email ?? "no user", "| error:", userError?.message ?? "none")

            if (user) {
              log("User found via getUser(), setting and fetching profile")
              set({ user, isGuest: false, sessionVerified: true })
              await get().refreshProfile(user)
            } else {
              log("No user found — setting guest mode")
              set({ user: null, profile: null, isGuest: true, sessionVerified: true })
            }
          }
        } catch (error) {
          err("initialize() threw:", error)
          // Don't set guest mode on error - might be network issue. Just stop
          // loading — sessionVerified stays false, since we genuinely don't
          // know if the cached `user` (if any) is still actually signed in.
          set({ isLoading: false })
        } finally {
          clearTimeout(timeoutId)
          const after = get()
          log(
            "=== initialize() END === user=",
            after.user?.email ?? "null",
            "role=",
            after.profile?.role ?? "null",
            "isGuest=",
            after.isGuest,
          )
          set({ isLoading: false })
        }
      },

      refreshProfile: async (forceUser?: User) => {
        const { user, profile: existingProfile } = get()
        const targetUser = forceUser || user
        log("--- refreshProfile() START --- user=", targetUser?.email ?? "null")

        if (!targetUser) {
          warn("refreshProfile called with no user — skipping")
          return
        }

        // If we already have a valid cached profile for this user, skip the query
        // unless forceUser was explicitly passed (i.e. a fresh login).
        // NOTE: We do NOT skip if forceUser is undefined but the profile is stale —
        // callers that mutate profile data (creator mode, settings) should pass forceUser
        // or call refreshProfile() directly to bust the cache.
        if (!forceUser && existingProfile && existingProfile.id === targetUser.id) {
          log("Using cached profile for", targetUser.email, "role=", existingProfile.role)
          return
        }

        try {
          log("Fetching profile from server API...")
          const startTime = Date.now()
          
          // Use server-side API instead of direct client query
          const response = await fetch("/api/auth/profile")
          const queryTime = Date.now() - startTime
          
          if (!response.ok) {
            let errorData
            try {
              errorData = await response.json()
            } catch {
              errorData = { error: `HTTP ${response.status}: ${response.statusText}` }
            }
            err("Profile API error:", errorData)
            warn("Keeping existing profile to preserve role. Existing role:", existingProfile?.role ?? "none")
            return
          }
          
          const { profile } = await response.json()
          
          log(
            `Profile fetched in ${queryTime}ms:`,
            JSON.stringify({
              found: !!profile,
              id: profile?.id,
              username: profile?.username,
              email: profile?.email,
              role: profile?.role,
              is_verified: profile?.is_verified,
            }, null, 2)
          )

          if (profile) {
            log("✅ Setting profile: username=", profile.username, "role=", profile.role, "email=", profile.email)
            set({ profile: profile as UserProfile })
            syncAllCredentialsToAccount().catch((e) => err("syncAllCredentials failed:", e))
            // Sync premium status to ad store so ads are gated correctly.
            // Used to be a local checkIsPremium() that only looked at the
            // tier label, never subscription_status -- so a canceled
            // subscriber stayed ad-free indefinitely if their tier field
            // hadn't been cleared yet (a real, ongoing revenue gap, not just
            // style drift: lib/entitlements.ts's hasPremiumAccess() was
            // already built to fix exactly this class of bug for 3 OTHER
            // surfaces that used to disagree with each other -- this was
            // the 4th surface still on the old, looser check). hasPremiumAccess
            // itself covers admin/dev/moderator/super_admin (isAdFreeStaffRole)
            // for the ad-free perk specifically -- staff get no other boost.
            const isPremium = hasPremiumAccess(profile as any)
            import("@/lib/ad-store").then(({ useAdStore }) => {
              useAdStore.getState().setIsPremiumUser(isPremium)
            }).catch(() => {})

            // Daily XP should never block page loading.
            fetch("/api/discord/daily-login-check")
              .then(async (loginCheckResponse) => {
                if (!loginCheckResponse.ok) return
                const { shouldAward } = await loginCheckResponse.json()
                if (!shouldAward) return
                const awardResponse = await fetch("/api/discord/daily-login-check", { method: "POST" })
                if (awardResponse.ok) {
                  const { xpAwarded, gooncoinAwarded, gooncoinNewBalance } = await awardResponse.json()
                  log(`Daily login XP awarded: +${xpAwarded} XP`)
                  if (gooncoinAwarded > 0) {
                    get().setDailyGooncoinClaim({ amount: gooncoinAwarded, newBalance: gooncoinNewBalance })
                  }
                }
              })
              .catch((xpError) => log("Daily login XP check failed (non-blocking):", xpError))
          } else {
            warn("⚠️ Profile row NOT FOUND for user id=", targetUser.id, "— setting profile to null.")
            warn("This usually means the profile wasn't created when the user signed up")
            warn("Check the handle_new_user() trigger function")
            set({ profile: null })
          }
        } catch (error) {
          err("refreshProfile() threw:", error)
          warn("Preserving existing profile due to error. Role:", existingProfile?.role ?? "none")
        }

        const after = get()
        log("--- refreshProfile() END --- role=", after.profile?.role ?? "null (no profile!)")
      },

      signOut: async () => {
        log("signOut() called — clearing local state immediately")

        // Set this account's scripts aside (never deleted: they are on the account too) so they are not left
        // visible to the next person on this browser, and keep the cloud sync from reading the emptied list.
        try {
          const swap = await import("@/lib/local-scripts-swap")
          swap.stashLocalScriptsForSignedOutVisitor()
        } catch {}

        // Clear local state FIRST — don't wait on any network call.
        // This makes logout instant and reliable regardless of network/Supabase latency.
        if (typeof window !== "undefined") {
          // User-specific store keys that must be wiped on logout so the next
          // account on this device doesn't inherit another user's data.
          const USER_SPECIFIC_KEYS = [
            "goonru-auth",
            "goonru-auth-storage",
            "goonru-cart",
            "goonru-drafts",
            "goonru-game-arena",
            "favorites",
            "folders",
            "user-ratings",
            "user-reputation-storage",
            "user-profile-storage",
            "goonpals-global-companion",
            "booru-credentials-storage",
            "background-searches",
          ]
          USER_SPECIFIC_KEYS.forEach((k) => localStorage.removeItem(k))

          // Also clear any legacy / Supabase-generated keys
          Object.keys(localStorage)
            .filter((k) => k.startsWith("sb-") || k === "auth-storage")
            .forEach((k) => {
              log("Removing localStorage key:", k)
              localStorage.removeItem(k)
            })
        }

        set({ user: null, profile: null, isGuest: true, sessionVerified: true })
        // Clear premium status on sign out
        import("@/lib/ad-store").then(({ useAdStore }) => {
          useAdStore.getState().setIsPremiumUser(false)
        }).catch(() => {})
        
        resetClient()

        // Call server-side signout to clear cookies
        try {
          log("Calling server-side signout API to clear cookies...")
          await fetch("/api/auth/signout", { method: "POST" })
          log("Server-side signout complete")
        } catch (e) {
          err("Server-side signout error:", e)
        }

        // Also call client-side signOut as backup
        const supabase = createClient()
        supabase.auth.signOut().catch((e: unknown) => err("Client signOut error (non-blocking):", e))

        log("Redirecting to /")
        // Small delay to ensure everything is cleared before redirect
        await new Promise(resolve => setTimeout(resolve, 200))
        window.location.href = "/"
      },
    }),
    {
      name: "goonru-auth-storage", // Unique storage key for Zustand
      storage: createJSONStorage(() => quotaSafeStorage),
      // FIX: Actually persist user and profile so they survive a page reload.
      // Previously partialize returned {} which saved nothing, causing logout on refresh.
      partialize: (state) => ({
        user: state.user,
        profile: state.profile,
        isGuest: state.isGuest,
      }),
    },
  ),
)

// Helper hooks
export function useUser() {
  return useAuthStore((state) => state.user)
}

export function useProfile() {
  return useAuthStore((state) => state.profile)
}

export function useIsGuest() {
  return useAuthStore((state) => state.isGuest)
}

export function useIsAdmin() {
  const profile = useAuthStore((state) => state.profile)
  const result = profile?.role === "admin" || profile?.role === "dev" || profile?.role === "super_admin"
  return result
}

export function useIsModerator() {
  const profile = useAuthStore((state) => state.profile)
  return (
    profile?.role === "moderator" ||
    profile?.role === "admin" ||
    profile?.role === "dev" ||
    profile?.role === "super_admin"
  )
}
